import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { endpoints } from '../../utils/axios';
import { useRouter } from '../../routes/hooks';
import { paths } from '../../routes/paths';

import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';
const STORAGE_USER_KEY = 'user';
const STORAGE_ROLE_KEY = 'role';

export function AuthProvider({ children }) {
  const router = useRouter();

  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        const userData = localStorage.getItem(STORAGE_USER_KEY);
        const userRole = localStorage.getItem(STORAGE_ROLE_KEY);
        const dataUser = JSON.parse(userData);
        const roleUser = JSON.parse(userRole);

        setSession(accessToken, dataUser, roleUser);

        dispatch({
          type: 'INITIAL',
          payload: {
            user: {
              ...dataUser,
              accessToken,
              role: roleUser,
            },
          },
        });
      } else {
        localStorage.clear();
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
        router.push(paths.auth.login);
      }
    } catch (error) {
      localStorage.clear();
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
      router.push(paths.auth.login);
    }
  }, [router]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
const login = useCallback(async (token, username) => {
    try {
      // const csrfToken = getCookie('csrf_token'); // Leer la cookie CSRF
      axios.defaults.headers.common.Authorization = `${token}`;

      const response = await axios.post(
        endpoints.api.auth.login,
        { username },
        {
          headers: { 'Content-Type': 'application/json' },
          // headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
          withCredentials: true,
        }
      );
      setSession(token, username, response.data.role);

      dispatch({
        type: 'LOGIN',
        payload: {
          user: {
            username,
            token,
            roleUser: response.data.role,
          },
        },
      });

      return { loged: true, loginError: '' };
    } catch (error) {
      return { loged: false, loginError: error?.response?.data?.message };
    }
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    console.log('LOGOUT LLEVA A ', paths.auth.login);

    // disconnectSocket();
    sessionStorage.clear();
    localStorage.clear();

    dispatch({
      type: 'LOGOUT',
    });
    router.push("/");
  }, [router]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      login,
      logout,
    }),
    [login, logout, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
